import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type ReactNodeViewProps } from '@tiptap/react';
import { useMediaBlobUrl } from '../../lib/mediaCache';
import { ResizableWrap } from './ResizableWrap';

// Single block-level media node that covers image / audio / pdf. Storing one
// node type keeps slash commands + drop handlers uniform; a `kind` attr picks
// the renderer. `mediaId` is always the R2-backed UUID returned by POST /media;
// we persist alt and contentType in attrs so renders work without a round-trip.
type MediaAttrs = {
  mediaId: string;
  kind: 'image' | 'audio' | 'pdf';
  alt: string;
  contentType: string;
  name: string;
  // Pixel width for images. null = natural/full-width. Stored on the node so
  // it survives save/reload and roundtrips through the HTML parser.
  width: number | null;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    media: {
      insertMedia: (attrs: Partial<MediaAttrs> & { mediaId: string; kind: MediaAttrs['kind'] }) => ReturnType;
    };
  }
}

export const Media = Node.create({
  name: 'media',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      mediaId: { default: '' },
      kind: { default: 'image' },
      alt: { default: '' },
      contentType: { default: '' },
      name: { default: '' },
      width: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-media-id]',
        getAttrs: (el) => {
          const node = el as HTMLElement;
          const widthStr = node.getAttribute('data-media-width');
          const width = widthStr ? Number(widthStr) : null;
          return {
            mediaId: node.getAttribute('data-media-id') ?? '',
            kind: (node.getAttribute('data-media-kind') as MediaAttrs['kind']) ?? 'image',
            alt: node.getAttribute('data-media-alt') ?? '',
            contentType: node.getAttribute('data-media-type') ?? '',
            name: node.getAttribute('data-media-name') ?? '',
            width: Number.isFinite(width) ? width : null,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-media-id': HTMLAttributes.mediaId,
        'data-media-kind': HTMLAttributes.kind,
        'data-media-alt': HTMLAttributes.alt,
        'data-media-type': HTMLAttributes.contentType,
        'data-media-name': HTMLAttributes.name,
        'data-media-width': HTMLAttributes.width ?? '',
      }),
    ];
  },

  addCommands() {
    return {
      insertMedia:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: 'media', attrs }),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(MediaView);
  },
});

function MediaView({ node, updateAttributes, editor }: ReactNodeViewProps<HTMLElement>) {
  const attrs = node.attrs as MediaAttrs;
  const { mediaId, kind, alt, name, width } = attrs;
  const src = useMediaBlobUrl(mediaId);
  const editable = editor?.isEditable ?? false;

  return (
    <NodeViewWrapper>
      <div className="media-block" data-kind={kind} contentEditable={false}>
        {kind === 'image' && (
          <ResizableImage
            src={src}
            alt={alt || name || ''}
            width={width}
            editable={editable}
            onResize={(w) => updateAttributes({ width: w })}
          />
        )}
        {kind === 'audio' && (
          <div className="media-audio">
            {src ? <audio src={src} controls preload="metadata" /> : <MediaPlaceholder />}
            {name && <div className="media-caption">{name}</div>}
          </div>
        )}
        {kind === 'pdf' && (
          <div className="media-pdf">
            {src ? (
              // Viewer chrome hidden: #toolbar=0 disables the built-in download/print
              // buttons in most browsers' PDF viewers. Combined with a blob: URL,
              // this stops the user from easily opening the file outside the app.
              <iframe
                src={`${src}#toolbar=0&navpanes=0&scrollbar=0`}
                title={name || 'PDF'}
                className="media-pdf-frame"
              />
            ) : (
              <MediaPlaceholder tall />
            )}
            <div className="media-caption">{name || 'PDF'}</div>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}

function MediaPlaceholder({ tall }: { tall?: boolean } = {}) {
  return (
    <div
      style={{
        height: tall ? 320 : 48,
        background: 'rgba(24,22,15,0.04)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        color: 'rgba(24,22,15,0.4)',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      Loading…
    </div>
  );
}

function ResizableImage({
  src,
  alt,
  width,
  editable,
  onResize,
}: {
  src: string | null;
  alt: string;
  width: number | null;
  editable: boolean;
  onResize: (width: number | null) => void;
}) {
  return (
    <ResizableWrap
      width={width}
      onResize={onResize}
      editable={editable}
      className="media-image-wrap"
    >
      {src ? (
        <img src={src} alt={alt} className="media-image" draggable={false} />
      ) : (
        <MediaPlaceholder tall />
      )}
    </ResizableWrap>
  );
}
